<script setup lang="ts">
import { useUserStore } from '~/store/user'

/**
 * The PUBLIC checkout.
 *
 * Anyone can configure, price AND buy a server here, with no account to begin
 * with — the identity step is part of the flow rather than a wall in front of
 * it. The console used to bounce an anonymous visitor from `/billing/checkout`
 * straight to `/register?redirect=…`, so the first thing a prospective customer
 * was asked for was a nine-field billing address, before a single price. Most
 * of them left there.
 *
 * `layout: false` is what makes this reachable: the global auth middleware
 * returns early for a chromeless page, exactly as it does for `/changelog` and
 * the public VPN download page. Nothing else had to be unlocked, because the
 * API's catalog endpoints were built public from the start — their docblock has
 * said "the wizard needs to render prices for anonymous visitors before they
 * sign up" far longer than anything actually called them anonymously.
 *
 * This page is a SHELL. The checkout itself is `CheckoutExperience`, shared
 * with `/billing/checkout`, so there is exactly one implementation and the two
 * routes cannot drift about what a plan costs, what an OS requires, or how an
 * order is placed. All this file decides is the chrome: no dashboard sidebar
 * for someone who is not signed in yet.
 */
definePageMeta({ layout: false })

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const userStore = useUserStore()

/**
 * This route serves BOTH a first-time visitor and a customer who is already
 * signed in — someone returning from a social sign-in lands right back here.
 * Offering "Log in" to somebody who just logged in reads as though it did not
 * work, so the header says who they are instead.
 */
const signedIn = computed(
  () => userStore.sessionStatus === 'authenticated' && !!userStore.user,
)
const displayName = computed(() => {
  const client = userStore.user as any
  const full = [client?.firstname, client?.lastname].filter(Boolean).join(' ').trim()
  return full || client?.email || ''
})

useSeoMeta({
  title: () => t('billing.page.checkout.navTitle'),
  description: () => t('billing.page.checkout.intro.description'),
  // A console page that happens to be public, not a marketing landing page.
  // Indexing it is a deliberate decision with SEO copy behind it, not a side
  // effect of removing an auth redirect.
  robots: 'noindex, nofollow',
})
</script>

<template>
  <div class="min-h-screen bg-default">
    <!-- The brand row SCROLLS AWAY. It is a wordmark and a link to the
         console: worth seeing on arrival, worth nothing for the rest of the
         purchase, and on a phone it was costing a third of the space the
         sticky chrome had already taken. Only the tracker earns its keep at
         the top of the screen. -->
    <header class="border-b border-default bg-default">
      <div
        class="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6"
      >
        <AppLogo size="sm" />
        <!-- Signed in: say who, and offer the way back to the console.
             Anonymous: offer the way in.

             `neutral`, not the brand colour: a ghost button paints its label in
             `primary`, and the default brand purple measures 4.12:1 on white at
             12px. The brand colour is whitelabel-configurable
             (`NUXT_PUBLIC_PRIMARY_COLOR`), so the fix cannot be a hand-picked
             darker purple — it has to be a token that is legible whatever the
             deployment's brand is. -->
        <div v-if="signedIn" class="flex min-w-0 items-center gap-2">
          <UAvatar
            size="xs"
            :alt="displayName"
            icon="i-lucide-user"
            class="shrink-0"
          />
          <span class="hidden min-w-0 truncate text-sm text-toned sm:block">
            {{ displayName }}
          </span>
          <UButton
            :to="localePath('/billing')"
            variant="ghost"
            color="neutral"
            size="sm"
            icon="i-lucide-layout-dashboard"
            :label="$t('billing.page.checkout.header.console')"
          />
        </div>
        <UButton
          v-else
          :to="localePath({ path: '/login', query: { redirect: route.fullPath } })"
          variant="ghost"
          color="neutral"
          size="sm"
          :label="$t('page.login.formTitle')"
        />
      </div>
    </header>

    <!-- The flow is long, and the one thing a customer cannot do from halfway
         down it is see what they have left behind — so this, and only this,
         stays on screen.

         `data-checkout-overlay` marks what covers the top of the page: every
         jump the tracker makes has to land below it, and the offset is
         MEASURED from this element rather than hardcoded, so it follows
         whatever height the bar happens to be at this breakpoint.

         Full-bleed surface with the content centred inside it: constraining
         the sticky element itself would leave the page scrolling through the
         gutters on a wide screen. -->
    <div
      data-checkout-overlay
      class="sticky top-0 z-20 border-b border-default bg-default/85 backdrop-blur"
    >
      <CheckoutProgressTracker class="mx-auto max-w-7xl" />
    </div>

    <main class="mx-auto max-w-7xl px-0 py-4 sm:px-2 lg:py-6">
      <CheckoutExperience
        :key="typeof route.query.trial === 'string' ? route.query.trial.toUpperCase() : 'paid'"
        is-public
      />
    </main>
  </div>
</template>
